import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, CheckCircle, AlertTriangle, Lock } from "lucide-react";
import { useUserAuth } from "../context/UserAuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_261f6229-25c6-462a-bf7c-d1c15ef074eb/artifacts/rv1gzzra_logo%20v2%20xpro.png";

export default function ActivatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useUserAuth();

  const [checking, setChecking] = useState(true);
  const [tokenStatus, setTokenStatus] = useState(null); // { valid, reason, nombre_completo, email }
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/auth/activar/check?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then(setTokenStatus)
      .catch(() => setTokenStatus({ valid: false, reason: "network" }))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Las contraseñas no coinciden"); return; }
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres"); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/activar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Error al activar la cuenta"); return; }
      login(data.user, data.access_token);
      navigate("/onboarding/seguridad", { replace: true });
    } catch {
      setError("Error de conexión. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) return <LoadingScreen />;

  if (!tokenStatus?.valid) {
    const msgs = {
      invalid: "Este enlace de activación no es válido o ya fue utilizado.",
      expired: "Este enlace ha caducado (válido 7 días).",
      already_active: "Esta cuenta ya está activada. Puedes iniciar sesión directamente.",
      network: "No se pudo verificar el enlace. Comprueba tu conexión.",
    };
    return (
      <Screen>
        <div className="flex flex-col items-center text-center" data-testid="invalid-token-screen">
          <AlertTriangle size={40} className="text-[#F2C230] mb-4" />
          <h1 className="text-xl font-black mb-3">Enlace no válido</h1>
          <p className="text-white/55 text-sm max-w-xs leading-relaxed mb-6">
            {msgs[tokenStatus?.reason] || msgs.invalid}
          </p>
          {tokenStatus?.reason === "already_active" ? (
            <a href="/login" className="btn-gold">Iniciar sesión</a>
          ) : (
            <p className="text-white/35 text-xs">Solicita un nuevo enlace en recepción de XPRO FITNESS.</p>
          )}
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="w-full max-w-sm" data-testid="activate-form">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={16} className="text-[#F2C230]" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-white/50">Activación de cuenta</span>
        </div>
        <h1 className="text-2xl font-black mb-1">Hola, {tokenStatus.nombre_completo.split(" ")[0]}</h1>
        <p className="text-white/45 text-sm mb-7">
          Crea tu contraseña para acceder a la app de XPRO FITNESS.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="field-label">Contraseña</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
                data-testid="password-input"
                className="field-input pr-12"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="field-label">Confirmar contraseña</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repite la contraseña"
              required
              data-testid="confirm-input"
              className="field-input"
            />
          </div>
          {error && <p className="text-red-400 text-xs" data-testid="activate-error">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !password || !confirm}
            data-testid="activate-submit"
            className="w-full btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Activando..." : "Activar cuenta y continuar"}
          </button>
        </form>
      </div>
    </Screen>
  );
}

function LoadingScreen() {
  return (
    <Screen>
      <div className="w-8 h-8 border-2 border-[#F2C230] border-t-transparent rounded-full animate-spin" />
    </Screen>
  );
}

function Screen({ children }) {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 py-12">
      <img src={LOGO} alt="XPRO FITNESS" className="h-10 w-auto mb-10" />
      {children}
      <style>{`
        .field-label { display: block; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; font-weight: 700; color: rgba(255,255,255,0.5); margin-bottom: 6px; }
        .field-input { width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.12); color: #fff; padding: 12px 14px; font-size: 14px; outline: none; transition: border-color .2s; }
        .field-input:focus { border-color: #F2C230; }
        .btn-gold { display: inline-flex; align-items: center; justify-content: center; background: #F2C230; color: #000; font-weight: 900; font-size: 12px; letter-spacing: 0.25em; text-transform: uppercase; padding: 14px 32px; text-decoration: none; transition: background .2s; width: 100%; }
        .btn-gold:hover { background: #fff; }
      `}</style>
    </div>
  );
}
